import React from 'react';
import { Store } from 'react-notifications-component';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';

import { history } from 'helpers/history';
import { HTTP_PROTOCOL } from 'config';
import { settingsDispatches } from 'pages/settings/containers/settingsContainer/constants';
import { settingsActions } from 'pages/settings/containers/settingsContainer/actions';
import { HELP_BASE_URL, HUBSPOT_CHAT_SRC } from 'config';
import { USER_ROUTES } from 'helpers/constants';

export const configurePanelApp = (props) => {
  props.fetchServicesNotifications();
  props.fetchNotifications();
  props.fetchDropdownNotifications();
  props.getLmsCsrfToken();
  props.getStudioCsrfToken();
  props.getPanelCsrfToken();
  props.getCredentialsCsrfToken();
  props.fetchMAUSQuickStats();
  props.fetchEnrolledCoursesQuickStats();
  props.fetchCourseCompletionsQuickStats();
  props.getUserPreferences();

  if (props.panel_role === 'panel_admin' || props.panel_role === 'super_admin') {
    props.fetchUsers();
    props.fetchThemes();
  }
};

export const getHelpUrl = () => {
  return HELP_BASE_URL;
};

export const setOrganizationFromQueryParam = () => {
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const organization_url = searchParams.get('organization_url');

  if (organization_url !== null) {
    localStorage.setItem('organization_url', organization_url);
  }
};

export const getParamFromURL = (key) => {
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  return searchParams.get(key);
};

export const getBillingRedirectFromQueryParam = () => {
  const rtb = getParamFromURL('redirect_to_billing');

  if (rtb === 'true') {
    localStorage.setItem('redirect_to_billing', true);
  }
};

export const getPanelBackendUrl = () => {
  const organization_url = localStorage.getItem('organization_url');
  if (organization_url !== null) {
    return organization_url;
  }

  let panelBackendDomain = window.location.host;

  if (panelBackendDomain.indexOf('dashboard.') > -1) {
    panelBackendDomain = panelBackendDomain.replace('dashboard.', 'panel.');
    return `${HTTP_PROTOCOL}${panelBackendDomain}/`;
  }

  if (panelBackendDomain.indexOf('apps.') > -1) {
    panelBackendDomain = panelBackendDomain.split('apps.');
  } else if (panelBackendDomain.indexOf('panel.') > -1) {
    panelBackendDomain = panelBackendDomain.split('panel.');
  }

  if (panelBackendDomain.length > 1) {
    panelBackendDomain = panelBackendDomain[1].replace('3030', '9999');
    return `${HTTP_PROTOCOL}panel.${panelBackendDomain}/`;
  }
};

export const getPanelLmsBaseUrl = () => {
  return (localStorage.getItem('panel_lms_base_url') || '').trim();
};

export const getPanelOrganizationId = () => {
  return (localStorage.getItem('organization_id') || '').trim();
};

export const getStudioBaseUrl = () => {
  return (localStorage.getItem('studio_base_url') || '').trim();
};

export const getWordpressBaseUrl = () => {
  return (localStorage.getItem('wordpress_base_url') || '').trim();
};

export const getCredentialsBaseUrl = () => {
  return (localStorage.getItem('credentials_base_url') || '').trim();
};

export const getEdlyServiceUrl = (organization, service_key) => {
  let edlyServiceUrl = '/#';
  if (organization && organization.edly_services_urls && service_key in organization.edly_services_urls) {
    edlyServiceUrl = organization.edly_services_urls[service_key];
  }
  return edlyServiceUrl;
};

export const setServicesNotificationsCookiesExpiry = (display_status) => {
  Cookies.set('panel_services_notifications_status', display_status, { path: '/' });

  let midnight = new Date();
  midnight.setHours(23, 59, 59, 0);
  Cookies.set('panel_services_notifications_daily_status', true, { expires: midnight, path: '/' });
};

export const messageNotification = (type, title, message, duration = 5000, onScreen = true) => {
  // An HTML body (e.g. a raw 500 page) is not a message; the generic 500 pop-up covers it.
  if (typeof message === 'string' && !/^\s*</.test(message)) {
    Store.addNotification({
      title: title,
      message: message,
      type: type,
      insert: 'top',
      container: 'top-right',
      animationIn: ['animated', 'fadeIn'],
      animationOut: ['animated', 'fadeOut'],
      dismiss: {
        duration: duration,
        onScreen: onScreen
      }
    });
  }
};

export const removeSession = () => {
  localStorage.removeItem('edx_username');
};

export const validateLoginSession = (dispatch, error) => {
  if (!error.response) {
    return false;
  }

  let error_check = error.response.status === 403 || error.response.status === 401;
  let unauthorised = 'Authentication credentials were not provided.';

  if (error_check && unauthorised) {
    removeSession();
    dispatch(settingsDispatches.userSettingsFailure(error));

    if (error.response.status === 401) {
      history.push('/panel/login');
    }
    return true;
  }
};

export const validatePanelAccess = (panel_role) => {
  if (panel_role === 'panel_restricted') {
    history.push('/panel/401');
  }
};

export const yearsList = (min_year, max_year) => {
  return Array(max_year - min_year + 1)
    .fill()
    .map((_, index) => min_year + index)
    .reverse();
};

export const filterErrorMessages = (error) => {
  let error_messages = error.response.data.field_errors;
  return Object.keys(error_messages).map((key) => {
    return `${key.toUpperCase()}:${error_messages[key].user_message}`;
  });
};

export const setupOrganizationLocalStorage = (dispatch, organizations, setAcceptTermsConditions = true) => {
  const lmsUrlExists =
    organizations.data &&
    organizations.data.results &&
    organizations.data.results[0] &&
    organizations.data.results[0].edly_services_urls &&
    organizations.data.results[0].edly_services_urls.lms;
  if (lmsUrlExists) {
    let result = organizations.data.results[0];
    localStorage.setItem('panel_lms_base_url', result.edly_services_urls.lms);
    localStorage.setItem('edx_username', result.current_site_user);
    localStorage.setItem('organization_id', result.id);
    localStorage.setItem('organization_slug', result.slug);
    if (setAcceptTermsConditions) {
      localStorage.setItem('accept_terms_conditions', result.accept_terms_conditions);
    }

    let wordpressUrl = result.edly_services_urls.wordpress;
    if (typeof wordpressUrl !== 'undefined' && wordpressUrl !== '') {
      localStorage.setItem('wordpress_base_url', wordpressUrl);
    }

    let studioUrl = result.edly_services_urls.studio;
    if (typeof studioUrl !== 'undefined' && studioUrl !== '') {
      localStorage.setItem('studio_base_url', studioUrl);
    }

    let credentialsUrl = result.edly_services_urls.credentials;
    if (typeof credentialsUrl !== 'undefined' && credentialsUrl !== '') {
      localStorage.setItem('credentials_base_url', credentialsUrl);
    }

    dispatch(settingsActions.getPanelUserRoles());
  }
};

export const hubspotChat = (theme) => {
  const scriptExist = document.getElementById('hs-script-loader');
  if (scriptExist) {
    scriptExist.remove();
  }
  const styleExist = document.getElementById('hubspot-dark-mode-style');
  if (styleExist) {
    styleExist.remove();
  }

  if (theme === 'dark') {
    const parentStyle = document.createElement('style');
    parentStyle.id = 'hubspot-dark-mode-style';
    parentStyle.textContent = `
      #hubspot-conversations-iframe {
        filter: invert(0.9) hue-rotate(180deg) contrast(1.1);
      }
    `;
    document.head.appendChild(parentStyle);
  }

  const script = document.createElement('script');
  script.id = 'hs-script-loader';
  script.type = 'text/javascript';
  script.src = HUBSPOT_CHAT_SRC;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
};

export const clearHubSpot = () => {
  const scriptExist = document.getElementById('hs-script-loader');
  if (scriptExist) {
    scriptExist.remove();
  }
  const styleExist = document.getElementById('hubspot-dark-mode-style');
  if (styleExist) {
    styleExist.remove();
  }
};

export const SetUpMixpanel = (email, username, lms_role) => {
  mixpanel.identify(email);
  mixpanel.people.set_once({
    $email: email,
    $name: username,
    department: lms_role
  });
};

export const convertValueTo2DP = (value) => {
  return isNaN(value) ? value : parseFloat(value).toFixed(2);
};

export const clearLocalStorage = () => {
  const keys = [
    'edx_username',
    'lms_csrf_token',
    'panel_csrf_token',
    'studio_csrf_token',
    'credentials_csrf_token',
    'organization_id',
    'organization_slug',
    'organization_url',
    'accept_terms_conditions',
    'panel_lms_base_url',
    'wordpress_base_url',
    'studio_base_url'
  ];
  for (let key in keys) {
    localStorage.removeItem(key);
  }
};

export const escapeHtml = (unsafe) => {
  if (unsafe === null) {
    return '';
  }
  const entityMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#34;': '"',
    '&#39;': "'"
  };
  return String(unsafe).replace(/&amp;|&lt;|&gt;|&quot;|&#34;|&#39;/g, (match) => entityMap[match]);
};

export const checkUserRoleAccess = (panelRole, currentRoute) => {
  return USER_ROUTES[panelRole].some((route) => currentRoute.startsWith(route));
};

export const checkCurrentPath = (paths, currentPath) => {
  return paths.some((route) => currentPath.startsWith(route));
};

export const isNotificationVisible = (panel_role) => {
  return ['panel_admin', 'super_admin'].includes(panel_role);
};

const SERVER_ERROR_NOTIFICATION_WINDOW_MS = 10000; // ponytail: fixed window, revisit if outages need real backoff
let lastServerErrorShownAt = 0;

const showServerErrorNotification = () => {
  const now = Date.now();
  if (now - lastServerErrorShownAt < SERVER_ERROR_NOTIFICATION_WINDOW_MS) {
    return;
  }
  lastServerErrorShownAt = now;

  Store.addNotification({
    message: (
      <>
        Something went wrong. Please{' '}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          style={{ color: '#fff', textDecoration: 'underline' }}
          onClick={() => window.location.reload()}
        >
          Try again
        </button>{' '}
        or contact{' '}
        <a href="mailto:support@edly.io" style={{ color: '#fff', textDecoration: 'underline' }}>
          support@edly.io
        </a>
        .
      </>
    ),
    type: 'danger',
    insert: 'top',
    container: 'top-right',
    animationIn: ['animated', 'fadeIn'],
    animationOut: ['animated', 'fadeOut'],
    dismiss: {
      duration: SERVER_ERROR_NOTIFICATION_WINDOW_MS,
      onScreen: true,
      click: false,
      showIcon: true
    }
  });
};

// Shows a generic pop-up whenever the wrapped axios instance gets a 500 response.
// Applied to every axios instance in the app (see src/index.js and the axios.create()
// instances in services/integrations and helpers/cacheApiClient) so it fires regardless
// of which instance made the call.
export const attach500Handler = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 500) {
        showServerErrorNotification();
      }
      return Promise.reject(error);
    }
  );
};
